import type { ApiResponse, BootstrapConfig, ModelSummary, DealerSummary, MarketingDetailsResponse, ModelSpecsData } from './types';

/**
 * Absolute base URL for all API calls.
 *
 * When the builder is embedded on a third-party site via the script-tag
 * embed, `embed.ts` sets `window.__VC_API_URL__` to the absolute origin of
 * the API server before this module loads. We append "/api/v1" to build
 * fully-qualified request URLs so they reach the correct server even when
 * the embed is running on a different domain.
 *
 * In hosted / dev mode (same origin or Vite proxy) the value is empty,
 * which produces a relative "/api/v1" path — no change from before.
 */
const API_BASE = ((window as any).__VC_API_URL__ ?? (import.meta.env.VITE_API_URL as string | undefined) ?? '') + '/api/v1';

/**
 * Base URL prepended to relative image paths returned by the API (e.g. "/uploads/...").
 *
 * In production the UI SPA is served from the web root while uploads live under
 * the API's public folder (e.g. /api/uploads/). VITE_UPLOADS_URL is set at
 * build time (see deploy.mjs) to the API public root so that relative paths
 * resolve correctly against the right origin+path.
 *
 * In dev the Vite server proxies both /api and /uploads to the local PHP server,
 * so relative paths work as-is and no prefix is needed.
 */
const IMAGE_BASE: string = (import.meta.env.VITE_UPLOADS_URL as string | undefined)
  ?? ((window as any).__VC_API_URL__ ? (window as any).__VC_API_URL__ + '/api' : '');

/**
 * Convert a relative image path from the API (e.g. "/uploads/x.png") to an
 * absolute URL. Already-absolute URLs are returned unchanged.
 */
export function resolveImageUrl(path: string | null | undefined): string {
  if (!path) return IMAGE_BASE ? IMAGE_BASE + '/uploads/placeholder.png' : '/uploads/placeholder.png';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (!IMAGE_BASE) return path;
  return IMAGE_BASE + path;
}

/**
 * Shared fetch wrapper with auth header injection and error handling.
 */
async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };

  // Attach JWT if available
  const token = localStorage.getItem('vc_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Attach tenant ID if available
  const tenantId = localStorage.getItem('vc_tenant_id');
  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  const json: ApiResponse<T> = await response.json();

  if (!json.success) {
    throw new ApiError(json.error.message, json.error.code, json.error.fields);
  }

  return json.data;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly fields?: Record<string, string>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── Public API methods ──────────────────────────────────────

export async function loadBootstrapConfig(): Promise<BootstrapConfig> {
  return request<BootstrapConfig>('/builder/bootstrap');
}

export async function fetchModels(): Promise<ModelSummary[]> {
  return request<ModelSummary[]>('/models');
}

export async function fetchModelDetail(id: number): Promise<unknown> {
  return request<unknown>(`/models/${id}`);
}

export async function fetchMarketingDetails(slug: string): Promise<MarketingDetailsResponse> {
  return request<MarketingDetailsResponse>(`/models/slug/${encodeURIComponent(slug)}/marketing`);
}

export async function fetchModelSpecs(id: number): Promise<ModelSpecsData> {
  return request<ModelSpecsData>(`/models/${id}/specs`);
}

export async function fetchModelSpecsBySlug(slug: string): Promise<ModelSpecsData> {
  return request<ModelSpecsData>(`/models/slug/${encodeURIComponent(slug)}/specs`);
}

export async function submitQuote(data: unknown): Promise<unknown> {
  return request<unknown>('/quote', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function fetchNearbyDealers(postalCode: string, country = 'US'): Promise<DealerSummary[]> {
  const params = new URLSearchParams({ postal_code: postalCode, country });
  return request<DealerSummary[]>(`/dealers/nearby?${params}`);
}

export async function fetchDealersForLocator(postalCode = '', country = 'US'): Promise<DealerSummary[]> {
  const params = new URLSearchParams({ country });
  if (postalCode) params.set('postal_code', postalCode);
  return request<DealerSummary[]>(`/dealers/locator?${params}`);
}

export async function dealerLogin(email: string, password: string): Promise<{ token: string; role: string }> {
  const result = await request<{ token: string; expires_at: string; role: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  localStorage.setItem('vc_token', result.token);
  return result;
}

export function dealerLogout(): void {
  localStorage.removeItem('vc_token');
}
